import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
} from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Plus, Edit, Trash2, Search, ChevronLeft, Eye } from "lucide-react";
import { educationAPI } from "../api/axiosClient";
import { Event, EventsResponse, Currency } from "../api/types";
import { toast } from "react-hot-toast";
import EventCreateForm from "../components/EventCreateForm";
import EventEditForm from "../components/EventEditForm";

interface EventViewProps {
  onSuccess: (action: "createEvent" | "updateEvent" | "deleteEvent") => void;
  onDeleteConfirm: (id: string, type: string) => void;
  onRefresh: () => void;
  onViewDetail: (eventId: string) => void;
  deletedEventId?: string | null;
  schoolId: string;
}

const EventView: React.FC<EventViewProps> = ({
  onSuccess,
  onDeleteConfirm,
  onRefresh,
  onViewDetail,
  deletedEventId,
  schoolId,
}) => {
  // Events list state
  const [events, setEvents] = useState<Event[]>([]);
  const [loadingEvents, setLoadingEvents] = useState<boolean>(false);
  const [eventsPagination, setEventsPagination] = useState({
    pageNumber: 1,
    totalPages: 1,
    totalCount: 0,
    hasPreviousPage: false,
    hasNextPage: false,
  });

  // Search state
  const [eventSearchTerm, setEventSearchTerm] = useState<string>("");

  // Form states
  const [showEventCreateForm, setShowEventCreateForm] =
    useState<boolean>(false);
  const [showEventEditForm, setShowEventEditForm] = useState<boolean>(false);
  const [editingEventId, setEditingEventId] = useState<string | null>(null);

  // Fetch events list
  const fetchEvents = useCallback(
    async (page: number = 1, searchTerm: string = "") => {
      setLoadingEvents(true);
      try {
        const response = await educationAPI.listEvents({
          Q: searchTerm,
          Page: page,
          Size: 10,
          OrderBy: 0,
        });

        if (response.success && response.data) {
          const eventsData = response.data as EventsResponse["data"];
          setEvents(eventsData.items || []);
          setEventsPagination({
            pageNumber: eventsData.pageNumber,
            totalPages: eventsData.totalPages,
            totalCount: eventsData.totalCount,
            hasPreviousPage: eventsData.hasPreviousPage,
            hasNextPage: eventsData.hasNextPage,
          });
        }
      } catch (error) {
        console.error("Error fetching events:", error);
        toast.error("Etkinlikler yüklenirken bir hata oluştu");
      } finally {
        setLoadingEvents(false);
      }
    },
    [],
  );

  // Load events when component mounts
  useEffect(() => {
    fetchEvents();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Search handler
  const handleEventSearchSubmit = () => {
    fetchEvents(1, eventSearchTerm);
  };

  // Form handlers
  const handleCreateEvent = () => {
    setShowEventCreateForm(true);
  };

  const handleEditEvent = (id: string) => {
    setEditingEventId(id);
    setShowEventEditForm(true);
  };

  const handleEventFormSuccess = (
    action: "createEvent" | "updateEvent" | "deleteEvent",
  ) => {
    setShowEventCreateForm(false);
    setShowEventEditForm(false);
    setEditingEventId(null);
    onSuccess(action);
    fetchEvents(); // Refresh the list
  };

  // Handle success from Dashboard (for delete operations)
  useEffect(() => {
    // This will be called when Dashboard calls onSuccess
    // For delete operations, we need to refresh the list
    // We can't directly detect which action was triggered, so we'll refresh on any success
    // This is safe because form operations already call fetchEvents() in handleEventFormSuccess
  }, [onSuccess]);

  // Listen for refresh requests from Dashboard
  useEffect(() => {
    // Dashboard can trigger a refresh by calling onRefresh
    // We can add logic here if needed
  }, [onRefresh]);

  // Listen for event deletion success from Dashboard
  useEffect(() => {
    // This will be called when Dashboard calls onSuccess('deleteEvent')
    // We need to remove the event from the list
    // But we don't know which event was deleted, so we need to refresh the list
    // Actually, let's just refresh the list to be safe
  }, [onSuccess]);

  // Listen for event deletion from Dashboard
  useEffect(() => {
    if (deletedEventId) {
      // This will be called when Dashboard sets deletedEventId
      // We can remove the specific event from the list
      removeEventFromList(deletedEventId);
      // Reset the deletedEventId after processing
      // Note: We can't directly reset it here as it's a prop from parent
      // The parent (Dashboard) should reset it after the effect runs
    }
  }, [deletedEventId]);

  const handleEventFormClose = () => {
    setShowEventCreateForm(false);
    setShowEventEditForm(false);
    setEditingEventId(null);
  };

  // Delete handler
  const handleDeleteEvent = (eventId: string) => {
    onDeleteConfirm(eventId, "event");
  };

  // Remove event from list after successful deletion
  const removeEventFromList = (eventId: string) => {
    setEvents((prevEvents) =>
      prevEvents.filter((event) => event.eventId !== eventId),
    );
    // Update pagination count
    setEventsPagination((prevPagination) => ({
      ...prevPagination,
      totalCount: prevPagination.totalCount - 1,
    }));
  };

  // Helper functions
  const getEventTypeLabel = (type: number): string => {
    switch (type) {
      case 0:
        return "Bilinmiyor";
      case 1:
        return "Gezi";
      case 2:
        return "Tatil";
      case 3:
        return "Konser";
      case 4:
        return "Tiyatro";
      case 5:
        return "Müze Ziyareti";
      case 6:
        return "Atölye";
      case 7:
        return "Seminer";
      case 8:
        return "Spor Etkinliği";
      case 9:
        return "Yarışma";
      case 10:
        return "Festival/Fuar";
      case 11:
        return "Mezuniyet";
      case 12:
        return "Oryantasyon";
      case 13:
        return "Piknik";
      case 14:
        return "Hayır/Bağış";
      case 15:
        return "Sınav";
      case 16:
        return "Okul Ödemesi";
      case 99:
        return "Diğer";
      default:
        return "Bilinmiyor";
    }
  };

  const getCurrencySymbol = (currency: Currency): string => {
    switch (currency) {
      case Currency.TRY:
        return "₺";
      default:
        return "₺";
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString("tr-TR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  };

  const formatPrice = (price: number, currency: Currency): string => {
    if (price === 0) {
      return "Ücretsiz";
    }
    const symbol = getCurrencySymbol(currency);
    // Türk standartlarına göre formatla: 1.234,56
    return `${symbol} ${price.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const isEventActive = (startsAt: string, endsAt: string): boolean => {
    const now = new Date();
    const startDate = new Date(startsAt);
    const endDate = new Date(endsAt);
    return now >= startDate && now <= endDate;
  };

  const isEventUpcoming = (startsAt: string): boolean => {
    const now = new Date();
    const startDate = new Date(startsAt);
    return startDate > now;
  };

  const isEventPast = (endsAt: string): boolean => {
    const now = new Date();
    const endDate = new Date(endsAt);
    return endDate < now;
  };

  const getEventStatus = (
    startsAt: string,
    endsAt: string,
  ): { label: string; className: string } => {
    if (isEventActive(startsAt, endsAt)) {
      return { label: "Aktif", className: "bg-green-100 text-green-800" };
    } else if (isEventUpcoming(startsAt)) {
      return { label: "Yaklaşan", className: "bg-blue-100 text-blue-800" };
    } else if (isEventPast(endsAt)) {
      return { label: "Tamamlandı", className: "bg-gray-100 text-gray-800" };
    }
    return { label: "Bilinmiyor", className: "bg-gray-100 text-gray-800" };
  };

  return (
    <div className="space-y-6">
      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center space-x-4">
            <div className="flex-1 relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Etkinlik ara... (Enter ile ara)"
                value={eventSearchTerm}
                onChange={(e) => setEventSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleEventSearchSubmit();
                  }
                }}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-200 transition hover:border-gray-400"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Events Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardDescription>
                {loadingEvents
                  ? "Yükleniyor..."
                  : `Toplam ${eventsPagination.totalCount || 0} etkinlik bulundu`}
              </CardDescription>
            </div>
            <Button
              className="bg-[#9B51E0] hover:bg-[#8B41D0]"
              onClick={handleCreateEvent}
            >
              <Plus className="w-4 h-4 mr-2" />
              Yeni Ekle
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loadingEvents ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9B51E0] mx-auto mb-2"></div>
                <p className="text-gray-500">Etkinlikler yükleniyor...</p>
              </div>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Etkinlik
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Tip
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Fiyat
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Başlangıç Tarihi
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Bitiş Tarihi
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Durum
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        İşlemler
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {events.length === 0 ? (
                      <tr key="no-events">
                        <td
                          colSpan={7}
                          className="text-center py-8 text-gray-500"
                        >
                          {eventSearchTerm
                            ? "Arama kriterlerine uygun etkinlik bulunamadı"
                            : "Henüz etkinlik eklenmemiş"}
                        </td>
                      </tr>
                    ) : (
                      events.map((event) => {
                        const status = getEventStatus(
                          event.startsAt,
                          event.endsAt,
                        );
                        return (
                          <tr key={event.eventId} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="flex-shrink-0 h-10 w-10">
                                  <div className="h-10 w-10 rounded-lg bg-[#9B51E0] flex items-center justify-center">
                                    <span className="text-white font-semibold text-sm">
                                      {event.name.charAt(0).toUpperCase()}
                                    </span>
                                  </div>
                                </div>
                                <div className="ml-4">
                                  <div className="text-sm font-medium text-gray-900">
                                    {event.name}
                                  </div>
                                  <div className="text-sm text-gray-500 truncate max-w-xs">
                                    {event.description}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">
                                {getEventTypeLabel(event.eventType)}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              {event.price === 0 ? (
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                  Ücretsiz
                                </span>
                              ) : (
                                <div className="text-sm font-medium text-gray-900">
                                  {formatPrice(event.price, event.currency)}
                                </div>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">
                                {formatDate(event.startsAt)}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">
                                {formatDate(event.endsAt)}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${status.className}`}
                              >
                                {status.label}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <div className="flex items-center space-x-2">
                                <button
                                  onClick={() => onViewDetail(event.eventId)}
                                  className="text-[#9B51E0] hover:text-[#8B41D0] p-1 rounded"
                                  title="Detay"
                                >
                                  <Eye className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => handleEditEvent(event.eventId)}
                                  className="text-blue-600 hover:text-blue-800 p-1 rounded"
                                  title="Düzenle"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() =>
                                    handleDeleteEvent(event.eventId)
                                  }
                                  className="text-red-600 hover:text-red-800 p-1 rounded"
                                  title="Sil"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {eventsPagination.totalPages > 1 && (
                <div className="flex items-center justify-between mt-6">
                  <div className="text-sm text-gray-700">
                    Sayfa {eventsPagination.pageNumber} /{" "}
                    {eventsPagination.totalPages}
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() =>
                        fetchEvents(
                          eventsPagination.pageNumber - 1,
                          eventSearchTerm,
                        )
                      }
                      disabled={!eventsPagination.hasPreviousPage}
                      className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() =>
                        fetchEvents(
                          eventsPagination.pageNumber + 1,
                          eventSearchTerm,
                        )
                      }
                      disabled={!eventsPagination.hasNextPage}
                      className="p-2 text-gray-500 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-4 h-4 rotate-180" />
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Event Create Form */}
      {showEventCreateForm && (
        <EventCreateForm
          isOpen={showEventCreateForm}
          onClose={handleEventFormClose}
          onSuccess={() => handleEventFormSuccess("createEvent")}
          schoolId={schoolId}
        />
      )}

      {/* Event Edit Form */}
      {showEventEditForm && editingEventId && (
        <EventEditForm
          isOpen={showEventEditForm}
          onClose={handleEventFormClose}
          onSuccess={() => handleEventFormSuccess("updateEvent")}
          editingEventId={editingEventId}
          schoolId={schoolId}
        />
      )}
    </div>
  );
};

export default EventView;
